import { useState, useEffect, useRef, FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/d8o03l880ubi47thma70";

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [serverError, setServerError] = useState<string>("");
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("opacity-100", "translate-y-0");
          el.classList.remove("opacity-0", "translate-y-8");
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const validate = (data: FormData): FormErrors => {
    const errs: FormErrors = {};
    if (!data.name.trim()) errs.name = "Veuillez indiquer votre nom";
    if (!data.email.trim()) {
      errs.email = "Veuillez indiquer votre email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errs.email = "Veuillez entrer une adresse email valide";
    }
    if (!data.phone.trim()) errs.phone = "Veuillez indiquer votre téléphone";
    if (!data.message.trim()) errs.message = "Veuillez décrire votre projet";
    return errs;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (serverError) setServerError("");
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldErrors = validate({ ...formData, [name]: e.target.value });
    if (fieldErrors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: fieldErrors[name as keyof FormErrors],
      }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setServerError("");
    setTouched({ name: true, email: true, phone: true, message: true });
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append("name", formData.name.trim());
      params.append("email", formData.email.trim());
      params.append("phone", formData.phone.trim());
      params.append("message", formData.message.trim());

      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setServerError(
          "L'envoi a échoué. Veuillez réessayer dans un instant.",
        );
      }
    } catch {
      setServerError(
        "L'envoi a échoué. Veuillez réessayer dans un instant.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full px-5 py-4 bg-white/60 border border-white/30 rounded-xl text-[0.95rem] text-foreground-900 placeholder:text-foreground-400 outline-none transition-all duration-500 ease-out";
  const inputFocus =
    "focus:bg-white/90 focus:border-primary-400/40 focus:shadow-[0_0_24px_rgba(234,81,73,0.06)] focus:ring-0";
  const inputError = "border-primary-400/40 bg-primary-50/40";

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="pt-0 pb-14 md:pb-20 relative opacity-0 translate-y-8 transition-all duration-700 bg-gradient-coral"
    >
      {/* Subtle depth overlay for the gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10 pointer-events-none" />

      <div className="pt-4 md:pt-8 pb-14 md:pb-20">
        <div className="max-w-[780px] mx-auto px-6 relative z-10">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-3xl md:text-[2.5rem] font-playfair font-bold text-white mb-3 md:mb-5 leading-tight">
              PARLONS DE VOTRE PROJET
            </h2>
            <p className="text-lg md:text-xl text-white/70 max-w-xl mx-auto leading-relaxed">
              Que votre projet soit déjà défini ou encore à imaginer, nous sommes là
              pour lui donner vie.
            </p>
          </div>

          <div className="relative bg-background-50/90 backdrop-blur-[12px] border border-white/10 rounded-3xl p-8 sm:p-10 md:p-14 shadow-[0_8px_40px_rgba(0,0,0,0.08)] transition-all duration-700">
            {/* Subtle inner warm glow */}
            <div
              className="absolute inset-0 rounded-3xl pointer-events-none opacity-20"
              style={{
                background:
                  "radial-gradient(ellipse at 50% 0%, rgba(139,59,59,0.04) 0%, transparent 60%)",
              }}
            />

            <div className="relative z-10">
              {!submitted ? (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-7"
                  noValidate
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="group">
                      <label className="block text-[0.8rem] font-medium tracking-wide uppercase text-foreground-600 mb-2.5 ml-1">
                        Nom complet <span className="text-primary-500 ml-0.5">*</span>
                      </label>
                      <input
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${inputBase} ${inputFocus} ${
                          touched.name && errors.name ? inputError : ""
                        }`}
                        placeholder="Votre nom et prénom"
                        autoComplete="name"
                      />
                      {touched.name && errors.name && (
                        <p className="text-[0.78rem] text-primary-500 mt-1.5 ml-1 transition-all duration-300">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="group">
                      <label className="block text-[0.8rem] font-medium tracking-wide uppercase text-foreground-600 mb-2.5 ml-1">
                        Email <span className="text-primary-500 ml-0.5">*</span>
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${inputBase} ${inputFocus} ${
                          touched.email && errors.email ? inputError : ""
                        }`}
                        placeholder="votre@email.com"
                        autoComplete="email"
                      />
                      {touched.email && errors.email && (
                        <p className="text-[0.78rem] text-primary-500 mt-1.5 ml-1 transition-all duration-300">
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="group">
                    <label className="block text-[0.8rem] font-medium tracking-wide uppercase text-foreground-600 mb-2.5 ml-1">
                      Téléphone <span className="text-primary-500 ml-0.5">*</span>
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`${inputBase} ${inputFocus} ${
                        touched.phone && errors.phone ? inputError : ""
                      }`}
                      placeholder="+41 79 327 51 34"
                      autoComplete="tel"
                    />
                    {touched.phone && errors.phone && (
                      <p className="text-[0.78rem] text-primary-500 mt-1.5 ml-1 transition-all duration-300">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  {/* Message */}
                  <div className="group">
                    <label className="block text-[0.8rem] font-medium tracking-wide uppercase text-foreground-600 mb-2.5 ml-1">
                      Décrivez votre projet <span className="text-primary-500 ml-0.5">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`${inputBase} ${inputFocus} resize-none ${
                        touched.message && errors.message ? inputError : ""
                      }`}
                      placeholder="Parlez-nous de votre idée, de votre envie ou de votre besoin. Même si votre projet n'est pas encore clairement défini, nous serons ravis d'en discuter avec vous."
                    />
                    <div className="flex justify-between items-center mt-1.5">
                      {touched.message && errors.message && (
                        <p className="text-[0.78rem] text-primary-500 ml-1 transition-all duration-300">
                          {errors.message}
                        </p>
                      )}
                      <p
                        className={`text-[0.75rem] ml-auto transition-colors duration-300 ${
                          formData.message.length >= 480
                            ? "text-primary-500"
                            : "text-foreground-400"
                        }`}
                      >
                        {formData.message.length}/500
                      </p>
                    </div>
                  </div>

                  {/* Server error banner */}
                  {serverError && (
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-primary-200/50 bg-primary-50/50 animate-fade-in">
                      <i className="ri-error-warning-line text-lg flex-shrink-0 mt-0.5 text-primary-500" />
                      <p className="text-[0.85rem] text-foreground-800 leading-relaxed">
                        {serverError}
                      </p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="text-center pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="group relative inline-flex items-center justify-center overflow-hidden rounded-full px-14 py-[1.125rem] text-[0.95rem] font-medium tracking-[0.08em] text-white transition-all duration-500 ease-out disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer bg-[#8B3B3B] shadow-[0_4px_20px_rgba(139,59,59,0.25)] hover:bg-[#7A2E2E] hover:shadow-[0_10px_35px_rgba(139,59,59,0.35)] hover:-translate-y-0.5"
                    >
                      {/* Hover glow overlay */}
                      <span className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                        style={{
                          background:
                            "radial-gradient(ellipse at center, rgba(255,255,255,0.12) 0%, transparent 70%)",
                        }}
                      />
                      {/* Shimmer effect */}
                      <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
                      <span className="relative z-10 flex items-center gap-3">
                        <span>
                          {submitting
                            ? "Envoi en cours..."
                            : "Parlons de votre projet"}
                        </span>
                        <i
                          className={`ri-arrow-right-line text-lg transition-transform duration-500 ${
                            submitting ? "" : "group-hover:translate-x-1"
                          }`}
                        />
                      </span>
                    </button>
                  </div>
                </form>
              ) : (
                <div className="py-14 md:py-20 flex flex-col items-center justify-center text-center animate-fade-in-up">
                  {/* Discreet confirmation icon */}
                  <div className="relative mb-10">
                    {/* Outer subtle ring */}
                    <div className="w-24 h-24 rounded-full flex items-center justify-center bg-gradient-to-br from-background-100 to-background-50 border border-background-200/60 animate-scale-in">
                      {/* Inner icon circle */}
                      <div className="w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br from-primary-400/25 to-primary-500/10">
                        <i className="ri-check-line text-2xl text-primary-500" />
                      </div>
                    </div>
                    {/* Floating subtle glow */}
                    <div
                      className="absolute inset-0 rounded-full pointer-events-none animate-glow-pulse opacity-30"
                      style={{
                        background:
                          "radial-gradient(circle, rgba(139,59,59,0.12) 0%, transparent 70%)",
                      }}
                    />
                  </div>

                  {/* Title */}
                  <h3 className="text-[1.35rem] md:text-[1.5rem] font-playfair font-semibold text-foreground-900 mb-4 tracking-wide animate-text-in">
                    Votre demande a bien été envoyée.
                  </h3>

                  {/* Decorative divider */}
                  <div className="flex items-center gap-3 mb-6 animate-text-in-delay">
                    <span className="block w-10 h-px bg-primary-300/50" />
                    <span className="block w-1 h-1 rounded-full bg-primary-400/40" />
                    <span className="block w-10 h-px bg-primary-300/50" />
                  </div>

                  {/* Body text */}
                  <p className="text-foreground-600 text-[0.95rem] md:text-base max-w-lg leading-[1.8] animate-text-in-delay font-light">
                    Merci pour votre message.
                  </p>
                  <p className="text-foreground-600 text-[0.95rem] md:text-base max-w-lg leading-[1.8] animate-text-in-delay font-light mt-2">
                    Nous avons bien reçu votre demande et reviendrons vers vous dans les meilleurs délais afin d&apos;échanger sur votre projet.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}